import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MDEditor from '@uiw/react-md-editor';
import rehypeSanitize from 'rehype-sanitize';
import PropTypes from 'prop-types';

import {
  setDraftBody,
  setDraftCategory,
  setDraftTitle,
} from '../../../store/slices/draftSlice';
import { sanitizeInput } from '../../../utils/sanitize';
import { Dialog } from '../../../layouts/Dialog';
import { useCreateThreadMutation } from '../../../store/api/threadsApi';
import { Button } from '../../pieces/Button';

export function NewThreadDialog() {
  const [createThread, { isLoading, isError, isSuccess, error }] =
    useCreateThreadMutation();
  const { title, category, body } = useSelector((state) => state.draft);
  const dispatch = useDispatch();

  const [isShown, setIsShown] = useState(false);
  const [withMarkdown, setWithMarkdown] = useState(false);
  const [markdownState, setMarkdownState] = useState('');

  const clean = withMarkdown
    ? sanitizeInput(markdownState)
    : sanitizeInput(body);
  const canSave = [title, body || markdownState].every(Boolean) && !isLoading;

  const onTitleChange = (e) => dispatch(setDraftTitle(e.target.value));
  const onCategoryChange = (e) => dispatch(setDraftCategory(e.target.value));
  const onBodyChange = (e) => {
    dispatch(setDraftBody(e.target.innerHTML));
  };

  const onClickSubmit = async (e) => {
    e.preventDefault();
    if (canSave) {
      await createThread({ title, body: clean, category });
    }
  };

  useEffect(() => {
    if (isSuccess) {
      document.getElementById('newThreadDialog').close();
      if (!withMarkdown) document.getElementById('body').innerHTML = '';
      if (withMarkdown) setMarkdownState('');
    }
    if (isError) {
      document.getElementById('createThreadFailed').show();
    }
  }, [isError, isSuccess, withMarkdown]);

  return (
    <>
      <Dialog dialogId="newThreadDialog">
        <form
          onSubmit={onClickSubmit}
          className={clsx(
            'fixed inset-0 my-auto mx-auto flex max-h-[80vh] max-w-lg flex-col overflow-hidden rounded-2xl bg-gray-100 p-2 dark:bg-gray-900 md:max-w-2xl',
            [isLoading ? 'opacity-90 blur-sm' : '']
          )}
        >
          <div className="sticky top-0 flex flex-row items-center justify-between bg-gray-100/90 py-2 dark:bg-gray-900/90">
            <div>
              <h3 className="p-2 text-lg font-semibold">New Thread</h3>
              {isError ? (
                <div className="px-2 text-xs text-red-500">
                  {error.message === 'Rejected'
                    ? 'Failed, please try again'
                    : error.message}
                </div>
              ) : (
                ''
              )}
            </div>
            <span className="sr-only">Create a new thread</span>
            {isShown ? (
              <div className="flex flex-row-reverse items-center gap-2 px-2">
                <Button
                  size="base"
                  variant="primary"
                  type="submit"
                  disabled={!canSave}
                >
                  {isLoading ? 'Submitting...' : 'Submit'}
                </Button>

                <Button
                  size="base"
                  variant="tertiary"
                  type="button"
                  onClick={() => {
                    setIsShown(false);
                    document.getElementById('newThreadDialog').close();
                  }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              ''
            )}
          </div>
          <div className="flex max-h-[80vh] flex-col space-y-4 overflow-scroll p-2">
            <div className="relative flex flex-col">
              <input
                type="text"
                id="title"
                name="title"
                value={title}
                onChange={onTitleChange}
                onInput={() => {
                  setIsShown(true);
                }}
                className="border-1 peer block w-full appearance-none rounded-lg border-gray-300/0 bg-transparent px-2.5 pb-2.5 pt-4 text-sm font-semibold text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-0 dark:border-gray-600/0 dark:text-white dark:focus:border-indigo-500"
                placeholder=" "
              />
              <label
                htmlFor="title"
                className="absolute top-2 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform bg-gray-100 px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-indigo-600 dark:bg-gray-900 dark:text-gray-400 peer-focus:dark:text-indigo-500"
              >
                Title
              </label>
            </div>
            <div className="relative flex flex-col">
              <input
                type="text"
                id="category"
                name="category"
                value={category}
                onChange={onCategoryChange}
                className="border-1 peer block w-full appearance-none rounded-lg border-gray-300/0 bg-transparent px-2.5 pb-2.5 pt-4 text-sm text-gray-900 focus:border-indigo-600 focus:outline-none focus:ring-0 dark:border-gray-600/0 dark:text-white dark:focus:border-indigo-500"
                placeholder=" "
              />
              <label
                htmlFor="category"
                className="absolute top-2 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform bg-gray-100 px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:px-2 peer-focus:text-indigo-600 dark:bg-gray-900 dark:text-gray-400 peer-focus:dark:text-indigo-500"
              >
                Category
              </label>
            </div>

            <div className="relative flex flex-col">
              {withMarkdown ? (
                <MDEditor
                  value={markdownState}
                  onChange={setMarkdownState}
                  textareaProps={{
                    name: 'textinput',
                    id: 'textinput',
                  }}
                  previewOptions={{
                    rehypePlugins: [[rehypeSanitize]],
                    className:
                      'bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100',
                  }}
                  className="texteditor bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-gray-100"
                  height={300}
                />
              ) : (
                <div
                  id="body"
                  name="body"
                  value={body}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={onBodyChange}
                  className="border-1 peer mb-4 block w-full appearance-none overflow-scroll rounded-lg border-gray-300/0 bg-transparent px-2.5 py-2 pb-2.5 pt-4 text-sm text-gray-900 [word-break:break-word] before:text-gray-900/50 empty:before:[content:'Add_content...'] focus:border-indigo-600 focus:outline-none focus:ring-0 focus-visible:outline-none dark:border-gray-600/0 dark:text-white dark:before:text-gray-100/60 dark:focus:border-indigo-500"
                />
              )}

              <label
                htmlFor="body"
                className="absolute top-2 left-1 z-10 origin-[0] -translate-y-4 scale-75 transform bg-gray-100 px-2 text-sm text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus-within:top-2 peer-focus-within:-translate-y-4 peer-focus-within:scale-75 peer-focus-within:px-2 peer-focus-within:text-indigo-600 dark:bg-gray-900 dark:text-gray-400 peer-focus-within:dark:text-indigo-500"
              >
                Body
              </label>
            </div>
            {isShown && (
              <div className="pl-4 text-xs">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setWithMarkdown(!withMarkdown);
                  }}
                >
                  {withMarkdown
                    ? 'Use default editor'
                    : 'Use markdown editor insead'}
                </button>
              </div>
            )}
          </div>
        </form>
      </Dialog>

      {isError ? <AddThreadError message={error.message.toString()} /> : ''}
      <Dialog dialogId="createThreadFailed">
        <div className="mt-[70vh] grid h-16 w-60 place-content-center bg-transparent">
          <div className="rounded-2xl bg-gray-50/70 px-3 py-2 text-sm backdrop-blur-sm dark:bg-gray-900/90">
            <h3>Ooops, something went wrong</h3>
            <p>Please try again...</p>
          </div>
        </div>
      </Dialog>
    </>
  );
}

function AddThreadError({ message = 'failed' }) {
  return (
    <Dialog dialogId="createThreadFailed">
      <div className="mt-[70vh] grid h-16 w-60 place-content-center bg-transparent">
        <div className="rounded-2xl bg-gray-50/70 px-3 py-2 text-sm backdrop-blur-sm dark:bg-gray-900/90">
          <h3>Ooops, something went wrong</h3>
          <p>{message.toString()}</p>
          <p>Please try again...</p>
        </div>
      </div>
    </Dialog>
  );
}

AddThreadError.propTypes = {
  message: PropTypes.string,
};
